"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeTransformNode = void 0;
const ValueObjects_1 = require("../ValueObjects");
const ObserverList_1 = require("./ObserverList");
function makeTransformNode() {
    return new TransformNodeImp();
}
exports.makeTransformNode = makeTransformNode;
class TransformNodeImp {
    constructor() {
        this.observers = new ObserverList_1.ObserverList();
        this._transformationMatrix = ValueObjects_1.Matrix.Identity();
        this.localPointToWorld = (point) => {
            return ValueObjects_1.Vector3.Transform(point, this.worldMatrix);
        };
        this.worldPointToLocal = (point) => {
            const worldInverse = ValueObjects_1.Matrix.Invert(this.worldMatrix);
            return ValueObjects_1.Vector3.Transform(point, worldInverse);
        };
        this.localDirectionToWorld = (point) => {
            const rotationMatrix = ValueObjects_1.Quaternion.ToRotationMatrix(this.worldRotation);
            return ValueObjects_1.Vector3.Transform(point, rotationMatrix).unit;
        };
        this.worldDirectionToLocal = (point) => {
            const worldInverse = ValueObjects_1.Matrix.Invert(this.worldMatrix);
            const rotationMatrix = ValueObjects_1.Quaternion.ToRotationMatrix(worldInverse.rotation);
            return ValueObjects_1.Vector3.Transform(point, rotationMatrix).unit;
        };
        this.chrono = 0;
        this.lerpDuration = 1;
        this.lerpEase = (p) => p;
        this.tickInterval = 10;
        this.lerp = (duration, ease, desiredPosition, desiredRotation, desiredScale, onCompletedCallback) => {
            this.cancelLerp();
            this.chrono = 0;
            this.lerpDuration = duration;
            this.lerpEase = ease;
            if (duration <= 0) {
                this.localMatrix = ValueObjects_1.Matrix.Compose(desiredScale !== null && desiredScale !== void 0 ? desiredScale : this.localScale, desiredRotation !== null && desiredRotation !== void 0 ? desiredRotation : this.localRotation, desiredPosition !== null && desiredPosition !== void 0 ? desiredPosition : this.localPosition);
                return;
            }
            if (desiredPosition) {
                this.lerpPos = [this.localPosition, desiredPosition];
            }
            else {
                this.lerpPos = undefined;
            }
            if (desiredRotation) {
                this.lerpQuat = [this.localRotation, desiredRotation];
            }
            else {
                this.lerpQuat = undefined;
            }
            if (desiredScale) {
                this.lerpScale = [this.localScale, desiredScale];
            }
            else {
                this.lerpScale = undefined;
            }
            this.onLerpCompletedCallback = onCompletedCallback;
            this.interval = setInterval(() => {
                this.tickLerp();
            }, this.tickInterval);
        };
        this.tickLerp = () => {
            this.chrono = this.chrono + this.tickInterval;
            let nomralizedTime = this.chrono / this.lerpDuration;
            if (nomralizedTime > 1) {
                nomralizedTime = 1;
            }
            const percent = this.lerpEase(nomralizedTime);
            let pos = this.localPosition;
            if (this.lerpPos) {
                pos = ValueObjects_1.Vector3.Lerp(this.lerpPos[0], this.lerpPos[1], percent);
            }
            let rotation = this.localRotation;
            if (this.lerpQuat) {
                rotation = ValueObjects_1.Quaternion.Slerp(this.lerpQuat[0], this.lerpQuat[1], percent);
            }
            let scale = this.localScale;
            if (this.lerpScale) {
                scale = ValueObjects_1.Vector3.Lerp(this.lerpScale[0], this.lerpScale[1], percent);
            }
            this._transformationMatrix = ValueObjects_1.Matrix.Compose(scale, rotation, pos);
            this.observers.notify();
            if (nomralizedTime >= 1 && this.interval) {
                if (this.onLerpCompletedCallback) {
                    this.onLerpCompletedCallback();
                }
                clearInterval(this.interval);
                this.interval = undefined;
            }
        };
        this.cancelLerp = () => {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = undefined;
            }
        };
        this.addObserver = (observer) => {
            this.observers.add(observer);
        };
        this.removeObserver = (observer) => {
            this.observers.remove(observer);
        };
    }
    get localMatrix() {
        return this._transformationMatrix;
    }
    set localMatrix(val) {
        if (ValueObjects_1.Matrix.Close(this._transformationMatrix, val))
            return;
        this.cancelLerp();
        this._transformationMatrix = val;
        this.observers.notify();
    }
    get worldMatrix() {
        if (!this._parent) {
            return this._transformationMatrix;
        }
        else {
            return ValueObjects_1.Matrix.Multiply(this._parent.worldMatrix, this._transformationMatrix);
        }
    }
    get localPosition() {
        return this._transformationMatrix.translation;
    }
    set localPosition(val) {
        const currentPos = this._transformationMatrix.translation;
        if (ValueObjects_1.Vector3.Close(val, currentPos, 0.00001))
            return;
        this.cancelLerp();
        this._transformationMatrix = ValueObjects_1.Matrix.SetMatrixTranslation(this._transformationMatrix, val);
        this.observers.notify();
    }
    get worldPosition() {
        return this.worldMatrix.translation;
    }
    set worldPosition(val) {
        if (!this._parent) {
            this.localPosition = val;
        }
        else {
            const local = this._parent.worldPointToLocal(val);
            this.localPosition = local;
        }
    }
    get localRotation() {
        return this._transformationMatrix.rotation;
    }
    set localRotation(val) {
        const currentRot = this._transformationMatrix.rotation;
        if (ValueObjects_1.Quaternion.Close(val, currentRot))
            return;
        this.cancelLerp();
        this._transformationMatrix = ValueObjects_1.Matrix.SetMatrixRotation(this._transformationMatrix, val);
        this.observers.notify();
    }
    get worldRotation() {
        return this.worldMatrix.rotation;
    }
    set worldRotation(val) {
        if (!this._parent) {
            this.localRotation = val;
        }
        else {
            const parentRotation = this._parent.worldRotation;
            const parentRotationInverse = ValueObjects_1.Quaternion.Inverse(parentRotation);
            this.localRotation = ValueObjects_1.Quaternion.Multiply(parentRotationInverse, val);
        }
    }
    get localScale() {
        return this._transformationMatrix.scale;
    }
    set localScale(val) {
        const currentScale = this._transformationMatrix.scale;
        if (ValueObjects_1.Vector3.Close(val, currentScale, 0.00001))
            return;
        this.cancelLerp();
        this._transformationMatrix = ValueObjects_1.Matrix.SetMatrixScale(this._transformationMatrix, val);
        this.observers.notify();
    }
    get worldScale() {
        return this.worldMatrix.scale;
    }
    set worldScale(val) {
        if (!this._parent) {
            this.localScale = val;
        }
        else {
            const parentScale = this._parent.worldScale;
            const x = val.x / parentScale.x;
            const y = val.y / parentScale.y;
            const z = val.z / parentScale.z;
            this.localScale = new ValueObjects_1.Vector3(x, y, z);
        }
    }
    get parent() {
        return this._parent;
    }
    set parent(val) {
        if (this._parent === val)
            return;
        const wMatrix = this.worldMatrix;
        this._parent = val;
        if (this._parent) {
            const parentMatrix = this._parent.worldMatrix;
            const parentMatrixInverse = ValueObjects_1.Matrix.Invert(parentMatrix);
            this.localMatrix = ValueObjects_1.Matrix.Multiply(parentMatrixInverse, wMatrix);
        }
        else {
            this.localMatrix = wMatrix;
        }
        this.observers.notify();
    }
}
